import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useApi, errMsg, fmtMoney } from "../lib/api";
import { COUNTRIES, COLOUR_HEX, TAG_TYPES } from "../lib/constants";
import { PayMethodSelect, PayInfo } from "../components/PayMethod";
import { useBilling } from "../lib/billing";
import { PRODUCTS, PRICE_KEY, useCart } from "../lib/site";
import { useMe } from "../components/AppShell";
import { PageHeader, Empty } from "../components/common";
import { CardBackPreview, CardHolder, GiftFields } from "../components/site/CheckoutExtras";

const NO_GIFT = { recipient_name: "", whatsapp: "", message: "" };

const CUR = { UG: "UGX", KE: "KES", TZ: "TZS", RW: "RWF" };

function RenewCheckout({ group, prices }) {
  const api = useApi();
  const nav = useNavigate();
  const [pay, setPay] = useState("mobile_money");
  const [busy, setBusy] = useState(false);
  const b = useBilling();
  const base = prices?.[group.currency]?.[group.type === "solo" ? "solo_subscription" : "group_subscription"];
  const renew = async () => {
    setBusy(true);
    try { const r = await api.post(`/groups/${group.id}/renew`, { payment_method: pay }); toast.success(`Invoice ${r.data.invoice_no} ready`); nav(`/app/g/${group.id}/orders`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="renew-checkout" className="max-w-xl">
      <PageHeader overline="Checkout" title="Renew subscription" />
      <div className="panel p-5 space-y-4">
        <div className="flex justify-between"><span className="font-semibold">{group.name} · {b?.period === "month" ? "1 month" : "1 year"}</span>
          <span data-testid="renew-price">{base != null ? fmtMoney(base, group.currency) : "…"}</span></div>
        <p className="text-xs text-slate-400">Any discount on your plan is applied on the invoice. Emergency pages keep working while you pay.</p>
        <PayMethodSelect value={pay} onChange={setPay} testid="renew-payment-select" />
        <PayInfo method={pay} testid="renew-payment-info" />
        <Button data-testid="renew-create-invoice-btn" disabled={busy} onClick={renew} className="bg-red-600 hover:bg-red-500 text-white px-8">Create renewal invoice</Button>
      </div>
    </div>
  );
}

export default function Checkout() {
  const api = useApi();
  const nav = useNavigate();
  const { groups, reload } = useMe();
  const { items, clear } = useCart();
  const mine = groups.filter((g) => g.my_role === "admin");
  const [gid, setGid] = useState("");
  const [country, setCountry] = useState("UG");
  const [pay, setPay] = useState("mobile_money");
  const [prices, setPrices] = useState(null);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState([]);
  const [holders, setHolders] = useState({});
  const [gifts, setGifts] = useState({});
  const [qs] = useSearchParams();
  const holder = qs.get("holder");
  useEffect(() => { api.get("/prices").then((r) => setPrices(r.data.prices)); }, [api]);
  useEffect(() => {
    setHolders({});
    if (!gid || gid === "new") { setMembers([]); return; }
    api.get(`/groups/${gid}/members`).then((r) => {
      setMembers(r.data);
      if (holder && r.data.some((m) => m.id === holder)) setHolders(Object.fromEntries(items.map((_, k) => [k, holder])));
    }).catch(() => setMembers([]));
  }, [api, gid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!gid) setGid((mine.find((g) => g.id === qs.get("g")) || mine.find((g) => g.type === "solo") || mine[0])?.id || "new"); }, [mine, gid, qs]);
  const renewGroup = qs.get("renew") && mine.find((g) => g.id === qs.get("g"));
  if (renewGroup) return <RenewCheckout group={renewGroup} prices={prices} />;
  if (!items.length) return <Empty testid="checkout-empty">Your cart is empty. <Link to="/shop" className="underline text-red-400">Go to the shop</Link></Empty>;
  const group = mine.find((g) => g.id === gid);
  const cur = group ? group.currency : CUR[country];
  const lines = items.map((i) => ({ ...i, p: PRODUCTS.find((x) => x.id === i.id) })).filter((l) => l.p);
  const total = prices ? lines.reduce((s, l) => s + prices[cur][PRICE_KEY[l.id]] * l.qty, 0) : 0;
  const place = async () => {
    setBusy(true);
    try {
      let target = gid;
      if (gid === "new") { const g = await api.post("/groups", { name: "My bsafe", type: "solo", country }); target = g.data.id; setGid(target); await reload(); }
      const r = await api.post(`/groups/${target}/shop-orders`, { items: lines.map((l, k) => ({ product_id: l.id, colour: l.colour, quantity: l.qty,
        person_id: holders[k] || null, gift: l.id === "egift" ? (gifts[k] || NO_GIFT) : null })), payment_method: pay });
      clear(); toast.success(`Invoice ${r.data.invoice_no} created`); nav(`/app/g/${target}/orders`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="checkout-page" className="max-w-3xl">
      <PageHeader overline="Shop" title="Checkout" />
      <div className="panel p-5 space-y-3" data-testid="checkout-lines">
        {lines.map((l, k) => <div key={`${l.id}-${l.colour}`} className="text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: COLOUR_HEX[l.colour] }} />{l.qty} × {l.p.name} <span className="text-slate-500">({l.colour})</span></span>
            <span className="font-mono">{prices ? fmtMoney(prices[cur][PRICE_KEY[l.id]] * l.qty, cur) : "…"}</span></div>
          {PRICE_KEY[l.id] in TAG_TYPES && <div className="flex flex-wrap gap-4 items-start mt-3 mb-2">
            {l.p.cat === "wallet" && <CardBackPreview testid={`card-back-preview-${k}`} name={(() => { const m = members.find((x) => x.id === holders[k]); return m ? `${m.first_name} ${m.last_name || ""}` : ""; })()} />}
            <CardHolder idx={k} card={l.p.cat === "wallet"} members={members} value={holders[k]} onChange={(v) => setHolders({ ...holders, [k]: v })} /></div>}
          {l.id === "egift" && <div className="mt-3 mb-2"><GiftFields idx={k} gift={gifts[k] || NO_GIFT} onChange={(g) => setGifts({ ...gifts, [k]: g })} /></div>}
        </div>)}
        <div className="flex justify-between border-t border-slate-700 pt-3 font-semibold"><span>Items total</span><span className="font-mono" data-testid="checkout-total">{fmtMoney(total, cur)}</span></div>
        <div className="text-xs text-slate-400">A yearly subscription is added to the invoice if you order tags and the account has none active.</div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div><div className="overline mb-2">Bill to</div>
          <Select value={gid} onValueChange={setGid}><SelectTrigger data-testid="checkout-group-select"><SelectValue /></SelectTrigger>
            <SelectContent>{mine.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} ({g.type})</SelectItem>)}<SelectItem value="new">New solo plan (just me)</SelectItem></SelectContent></Select></div>
        {gid === "new" ? <div><div className="overline mb-2">Country & currency</div>
          <Select value={country} onValueChange={setCountry}><SelectTrigger data-testid="checkout-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(COUNTRIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div> : <div />}
        <div><div className="overline mb-2">Payment</div>
          <PayMethodSelect value={pay} onChange={setPay} testid="checkout-payment-select" /></div>
      </div>
      <PayInfo method={pay} testid="checkout-payment-info" className="mt-4" />
      <Button data-testid="checkout-place-order-btn" disabled={busy || !prices} onClick={place} className="mt-6 bg-red-600 hover:bg-red-500 text-white px-8">Create invoice</Button>
    </div>
  );
}
